import { useCallback, useEffect, useRef, useState } from 'react';
import type { BotStatusDto } from '@rhcst/shared';
import { api, ApiRequestError } from './api.js';

/**
 * Bot status polling.
 *
 * This is the telemetry source the tally light and interlock strip have been
 * waiting for since Phase 1.
 *
 * Polling rather than a socket: the payload is small, the interval is slow, and
 * a WebSocket would add a reconnect state machine and a Plesk proxy
 * configuration for no benefit at this scale.
 *
 * On a failed poll the previous status is kept but marked `stale`. Blanking the
 * interlocks on a transient network hiccup would make an armed system briefly
 * render as "no reading", and the operator must never see a safer picture than
 * the truth.
 */

const POLL_INTERVAL_MS = 15_000;

export interface BotStatusState {
  status: BotStatusDto | null;
  loading: boolean;
  stale: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useBotStatus(): BotStatusState {
  const [status, setStatus] = useState<BotStatusDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Avoids a state update after unmount when a poll resolves late.
  const mounted = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const next = await api.get<BotStatusDto>('/bot/status');
      if (!mounted.current) return;
      setStatus(next);
      setStale(false);
      setError(null);
    } catch (err) {
      if (!mounted.current) return;
      if (err instanceof ApiRequestError && err.isUnauthorized) {
        // The session ended. The session provider handles the redirect; there is
        // nothing useful to show here.
        setStatus(null);
        setError(null);
      } else {
        setStale(true);
        setError('Could not reach the API. Showing the last known state.');
      }
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void refresh();

    const timer = setInterval(() => void refresh(), POLL_INTERVAL_MS);

    return () => {
      mounted.current = false;
      clearInterval(timer);
    };
  }, [refresh]);

  return { status, loading, stale, error, refresh };
}
